import React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { DeleteButton } from "@refinedev/mui";
import { Chip, IconButton, Tooltip } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router";

import { TableCell } from "@shared/components";
import { Partner, PartnerReferral, PartnerStatus } from "@modules/partners/types";
import { formatCurrency } from "@core/utils/masks";

const statusColor: Record<PartnerStatus, "warning" | "success" | "error"> = {
  pending: "warning",
  approved: "success",
  rejected: "error",
};

interface UseReferralsColumnsParams {
  translate: (key: string, options?: Record<string, unknown>) => string;
  refetch: () => void;
  partners?: Partner[];
  onApprove: (id: number) => void;
  onReject: (referral: PartnerReferral) => void;
}

export const useReferralsColumns = ({
  translate,
  refetch,
  partners,
  onApprove,
  onReject,
}: UseReferralsColumnsParams): GridColDef[] => {
  const navigate = useNavigate();

  return React.useMemo<GridColDef[]>(
    () => [
      {
        field: "partner",
        headerName: translate("v1/partner_referral_admin.fields.partner"),
        minWidth: 180,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Link
            to={`/partners/detail/${value}`}
            value={partners?.find((p) => p.id === value)?.name}
          />
        ),
      },
      {
        field: "company_name",
        headerName: translate("v1/partner_referral_admin.fields.company_name"),
        minWidth: 200,
        headerAlign: "left",
        renderCell: ({ value }) => <TableCell.Text value={value} textAlign="left" />,
      },
      {
        field: "company_cnpj",
        headerName: translate("v1/partner_referral_admin.fields.company_cnpj"),
        minWidth: 150,
        headerAlign: "left",
        renderCell: ({ value }) => <TableCell.Text value={value} textAlign="left" />,
      },
      {
        field: "sales_value",
        headerName: translate("v1/partner_referral_admin.fields.sales_value"),
        minWidth: 130,
        headerAlign: "right",
        renderCell: ({ value }) => (
          <TableCell.Text value={formatCurrency(value)} />
        ),
      },
      {
        field: "commission_percentage",
        headerName: translate("v1/partner_referral_admin.fields.commission_percentage"),
        minWidth: 130,
        headerAlign: "right",
        renderCell: ({ value }) => <TableCell.Text value={value} />,
      },
      {
        field: "commission_value",
        headerName: translate("v1/partner_referral_admin.fields.commission_value"),
        minWidth: 130,
        headerAlign: "right",
        renderCell: ({ value }) => (
          <TableCell.Text value={formatCurrency(value)} />
        ),
      },
      {
        field: "status",
        headerName: translate("v1/partner_referral_admin.fields.status"),
        minWidth: 130,
        headerAlign: "center",
        renderCell: ({ value }) => (
          <TableCell.Container align="center">
            <Chip
              size="small"
              color={statusColor[value as PartnerStatus] ?? "default"}
              label={translate(`partners.status.${value}`)}
            />
          </TableCell.Container>
        ),
      },
      {
        field: "created_at",
        headerName: translate("v1/partner_referral_admin.fields.created_at"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: ({ value }) => <TableCell.Date value={value} />,
      },
      {
        field: "actions",
        headerName: translate("table.actions"),
        display: "flex",
        headerAlign: "center",
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => (
          <TableCell.Container align="center">
            {row.status === "pending" && (
              <>
                <Tooltip title={translate("partners.actions.approve")}>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => onApprove(row.id)}
                  >
                    <CheckCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={translate("partners.actions.reject")}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onReject(row as PartnerReferral)}
                  >
                    <HighlightOffIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title={translate("buttons.edit")}>
              <IconButton
                size="small"
                onClick={() => navigate(`/partners/referrals/edit/${row.id}`)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <DeleteButton
              hideText
              recordItemId={row.id}
              resource="v1/partner_referral_admin"
              onSuccess={refetch}
            />
          </TableCell.Container>
        ),
      },
    ],
    [translate, refetch, partners, onApprove, onReject, navigate]
  );
};
